import { locationOrigin, toCamelCase } from 'booksprout'
import { useAxios } from '../modules/axios/axiosModule'
import { useI18n } from '../modules/i18n/i18nModule'
import { useSystem } from '../modules/system/systemModule'
import { MetaState } from '../modules/system/store/types'

/**
 * Page metadata for SSR.
 *
 * Two halves, both out of `CommonBaseMixin`'s component options: `fetchMetaForRoute` is what its
 * `preFetch` ran on the server to load a document's title and description before render, and
 * `setPageMeta` is what a page calls to set its own from a translation key.
 *
 * The canonical url is always built against `/reviewer`, hardcoded: the publisher and admin apps sit
 * behind a login where seo does not apply, so the reviewer app is the only one whose pages are indexed.
 */

export interface MetaRoute {
  params: { id?: string }
  path: string
  meta?: { metaInstructions?: { module?: string } }
}

/**
 * Only the router. The translator used to be passed in as `t: (key, args) => vm.$t(key, args)`, read
 * from the component -- and `$t` does not exist during `setup()`: vue-i18n 9's legacy API installs it
 * from a global mixin's `created` hook, which Vue 3 runs after setup. Every page that set its own
 * title threw "vm.$t is not a function" the moment it loaded. The app-level instance below has no such
 * problem. `$router`, by contrast, IS a global property from the moment the plugin is installed, and
 * is still taken from the component because under SSR the router is per request (see routingModule).
 */
export interface MetaContext {
  router: { currentRoute: { value: { path: string } } }
}

export function useMeta (context?: MetaContext) {
  /**
   * Server-side only: asks the api for the meta of whatever document this route displays. Was
   * hand-building a StoreService, a ConfigService and an AxiosService — one of the two places that
   * bypassed the DI container entirely — and is now just a request, because axiosModule reads the
   * config and the token itself.
   *
   * This is the ONLY thing the SSR render awaits, and it must never be allowed to hold the response.
   * It used to run on the shared client's 60s timeout, which is also nginx's `proxy_read_timeout` for
   * /reviewer — and since the render only starts once this resolves, a slow api consumed nginx's
   * whole budget before a single response header was written. That is not a slow page, it is a
   * guaranteed 504: Baiduspider got one on a book page at 05:01 on 23 Aug 2026, a minute into the
   * nightly backup, while its opening FLUSH TABLES was stalling every api query (sc-26631, gone
   * since 7 Oct 2026). There was no catch either, so an api that *failed* rather than stalled
   * rejected straight out of `preFetch` and served a 500. [sc-27959]
   *
   * 5s matches the `auth/live` probe in axiosModule — this is a single document lookup, so anything
   * beyond a second or two already means the api is unwell. Failing here costs the page its title and
   * description, which matters (these are the only indexed pages on the platform), but far less than
   * timing the crawler out and pinning an SSR worker for a minute while doing it.
   *
   * Warn, not error: the page degrades on its own, so a handful of these a month is not worth a bug
   * story. Note the level is the only thing being chosen here, not whether it alerts — bs-monitor's
   * docker scanner reads a MERGED stream, so it opens an error group only on a level token or a
   * `SomethingError:` header, and this message carries neither at any level. Keep it that way:
   * logging the raw axios error instead would put `AxiosError:` at the start of the line and file a
   * story per slow crawl.
   */
  const META_FETCH_TIMEOUT_MS = 5000

  const fetchMetaForRoute = async (currentRoute?: MetaRoute) => {
    if (!currentRoute?.meta?.metaInstructions?.module) {
      return
    }

    await useAxios().axios().post('auth/meta-info', {
      id: currentRoute.params.id,
      module: toCamelCase(currentRoute.meta.metaInstructions.module),
      moduleUrl: currentRoute.path
    }, { timeout: META_FETCH_TIMEOUT_MS }).then((r: { data: MetaState }) => {
      useSystem().system.service.setMeta(r.data)
    }).catch((e: { message?: string }) => {
      // Render without it. The page keeps the layout's default meta. Message first, error text as a
      // separate argument, so no `SomethingError:` header can lead the line — see above.
      console.warn(`Meta fetch failed for ${currentRoute.path}, rendering without it:`, e?.message ?? e)
    })
  }

  const setPageMeta = (module: string, title: string | undefined = void 0) => {
    if (context === void 0) {
      throw new Error('useMeta: setPageMeta needs the router.')
    }

    useSystem().system.service.setMeta({
      // `t`, not `tc`: i18n reads the '| Booksprout' in the template as a plural separator
      title: useI18n().t(`meta.module.${module}.title`, [title]),
      // not fullPath; and `.value` because vue-router 4 exposes currentRoute as a Ref
      canonicalUrl: locationOrigin() + '/reviewer' + context.router.currentRoute.value.path
    })
  }

  /**
   * What `CommonBaseMixin`'s `meta()` option used to return. It is now handed to Quasar's own
   * `useMeta()` from each layout's setup -- Quasar 2 dropped the `meta ()` component option, and
   * nothing calls it any more.
   * The og: tags are what the reviewer app's public arc and author pages get shared with.
   */
  const documentMeta = () => {
    const meta = useSystem().system.store.getters.meta

    return {
      title: meta.title,
      titleTemplate: (title: string) => `${title} | Booksprout`,
      meta: {
        description: {
          name: 'description',
          content: meta.description
        },
        keywords: {
          name: 'keywords',
          content: meta.keywords
        },
        ogTitle: {
          name: 'og:title',
          content: meta.ogTitle
        },
        ogImage: {
          name: 'og:image',
          content: meta.ogImage
        },
        ogImageWidth: {
          name: 'og:image:width',
          content: '1200'
        },
        ogImageHeight: {
          name: 'og:image:height',
          content: '630'
        },
        ogUrl: {
          name: 'og:url',
          content: meta.ogUrl
        }
      },
      link: {
        canonical: {
          rel: 'canonical',
          href: meta.canonicalUrl
        }
      }
    }
  }

  return {
    fetchMetaForRoute,
    setPageMeta,
    documentMeta
  }
}
